// redux
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "../config/api";
import config from "../config/config";
import { getCurrentLoggedInSessionToken } from "../config/userPool";

const { REWARDS_OPERATORS_ENDPOINT, REWARDS_PROVIDERS_ENDPOINT, AUDIT_ENDPOINT } = config.api_endpoint;

const initialState = {
  loading: false,
  operator_list: [],
  error: null,
  provider_loading: false,
  provide_list: [],
  provider_error: null,
  save_loading: false,
  save_data: {},
  save_error: null,
  detail_loading: false,
  detail_data: {},
  detail_error: null,
  audit_loading: false,
  audit_data: [],
  poc_user_list: [],
  poc_user_loading: false,
};

export const fetchAllOperatorList = createAsyncThunk(
  "RewardsOperator/fetchAllOperatorList",
  async (_, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      const results = await api.get(REWARDS_OPERATORS_ENDPOINT, {}, token);
      return results.data;
    } catch (error) {
      return rejectWithValue(error.details);
    }
  }
);

export const fetchAllProviderList = createAsyncThunk(
  "RewardsOperator/fetchAllProviderList",
  async (_, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      const results = await api.get(REWARDS_PROVIDERS_ENDPOINT, {}, token);
      return results.data;
    } catch (error) {
      return rejectWithValue(error.details);
    }
  }
);

export const fetchOperatorDetails = createAsyncThunk(
  "RewardsOperator/fetchOperatorDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      const results = await api.get(`${REWARDS_OPERATORS_ENDPOINT}/${id}`, {}, token);
      return results.data;
    } catch (error) {
      return rejectWithValue(error.details);
    }
  }
);

export const saveRewardsOperator = createAsyncThunk(
  "RewardsOperator/saveRewardsOperator",
  async (data, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      const results = await api.post(
        REWARDS_OPERATORS_ENDPOINT,
        JSON.stringify(data),
        token
      );
      return results;
    } catch (e) {
      return rejectWithValue(e.details);
    }
  }
);

export const updateRewardsOperator = createAsyncThunk(
  "RewardsOperator/updateRewardsOperator",
  async (data, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      let { id } = data;
      const results = await api.put(
        `${REWARDS_OPERATORS_ENDPOINT}/${id}`,
        JSON.stringify(data),
        token
      );
      return results;
    } catch (e) {
      return rejectWithValue(e.details);
    }
  }
);

export const deleteRewardsOperator = createAsyncThunk(
  "RewardsOperator/deleteRewardsOperator",
  async ({ id }, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      const results = await api.remove(
        `${REWARDS_OPERATORS_ENDPOINT}/${id}`,
        JSON.stringify({}),
        token
      );
      return results;
    } catch (e) {
      return rejectWithValue(e.details);
    }
  }
);

export const fetchRewardsOperatorAudit = createAsyncThunk(
  "RewardsOperator/fetchRewardsOperatorAudit",
  async (id, { rejectWithValue }) => {
    try {
      const token = await getCurrentLoggedInSessionToken();
      const results = await api.get(
        `${AUDIT_ENDPOINT}?table_name=gamifications-operator&source_id=${id}`,
        {},
        token
      );
      return results.data;
    } catch (err) {
      console.log(err);
      return rejectWithValue(err.details);
    }
  }
);



export const fetchPOCUserList = createAsyncThunk(
  "RewardsOperator/fetchPOCUserList",
  async (data, { rejectWithValue }) => {
    try {
      const token = getCurrentLoggedInSessionToken();
      let url = `${REWARDS_OPERATORS_ENDPOINT}/poc`;

      const results = await api.get(url, {}, token);

      return results;
    } catch (error) {
      return rejectWithValue(error.details);
    }
  }
);

const RewardsOperatorSlice = createSlice({
  name: "RewardsOperator",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllProviderList.pending, (state) => {
        state.provider_loading = true;
      })
      .addCase(fetchAllProviderList.fulfilled, (state, action) => {
        state.provider_loading = false;
        state.provide_list = action.payload;
      })
      .addCase(fetchAllProviderList.rejected, (state, action) => {
        state.provider_loading = false;
        state.provider_error = action.error.message;
      })
      .addCase(saveRewardsOperator.pending, (state) => {
        state.save_loading = true;
      })
      .addCase(saveRewardsOperator.fulfilled, (state, action) => {
        state.save_loading = false;
        state.save_data = action.payload;
      })
      .addCase(saveRewardsOperator.rejected, (state, action) => {
        state.save_loading = false;
        state.save_error = action.error.message;
      })
      .addCase(updateRewardsOperator.pending, (state) => {
        state.save_loading = true;
      })
      .addCase(updateRewardsOperator.fulfilled, (state, action) => {
        state.save_loading = false;
        state.save_data = action.payload;
      })
      .addCase(updateRewardsOperator.rejected, (state, action) => {
        state.save_loading = false;
        state.save_error = action.error.message;
      })
      .addCase(deleteRewardsOperator.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRewardsOperator.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteRewardsOperator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchOperatorDetails.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(fetchOperatorDetails.fulfilled, (state, action) => {
        state.detail_loading = false;
        state.detail_data = action.payload;
      })
      .addCase(fetchOperatorDetails.rejected, (state, action) => {
        state.detail_loading = false;
        state.detail_error = action.error.message;
      })
      .addCase(fetchAllOperatorList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllOperatorList.fulfilled, (state, action) => {
        state.loading = false;
        state.operator_list = action.payload;
      })
      .addCase(fetchAllOperatorList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchRewardsOperatorAudit.pending, (state) => {
        state.audit_loading = true;
      })
      .addCase(fetchRewardsOperatorAudit.fulfilled, (state, action) => {
        state.audit_loading = false;
        state.audit_data = action.payload;
      })
      .addCase(fetchRewardsOperatorAudit.rejected, (state, action) => {
        state.audit_loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPOCUserList.pending, (state) => {
        state.poc_user_loading = true;
        state.poc_user_list = [];
      })
      .addCase(fetchPOCUserList.fulfilled, (state, action) => {
        state.poc_user_loading = false;
        state.poc_user_list = action.payload.data;
      })
      .addCase(fetchPOCUserList.rejected, (state, action) => {
        state.poc_user_loading = false;
        state.poc_user_list = [];
        state.detail_error = action.error.message;
      })
      ;
  },
});

const RewardsOperatorReducer = RewardsOperatorSlice.reducer;
export default RewardsOperatorReducer;